import { useEffect, useRef } from 'react';

interface ShortcutOptions {
  key: string;
  meta?: boolean;
  ctrl?: boolean;
  shift?: boolean;
  prefix?: string;
  enabled?: boolean;
  onTrigger: () => void;
}

function matchesModifier(event: KeyboardEvent, key: string, meta?: boolean, ctrl?: boolean, shift?: boolean): boolean {
  const wantsMeta = meta ?? false;
  const wantsCtrl = ctrl ?? false;
  if (wantsMeta || wantsCtrl) {
    const modPressed = event.metaKey || event.ctrlKey;
    if (!modPressed) return false;
  }
  if (shift && !event.shiftKey) return false;
  if (event.key.toLowerCase() !== key.toLowerCase()) return false;
  return true;
}

export function useShortcut({
  key,
  meta,
  ctrl,
  shift,
  prefix,
  enabled = true,
  onTrigger,
}: ShortcutOptions) {
  const onTriggerRef = useRef(onTrigger);
  onTriggerRef.current = onTrigger;

  useEffect(() => {
    if (!enabled) return undefined;

    let prefixActive = false;
    let prefixTimer: number | undefined;

    const clearPrefix = () => {
      prefixActive = false;
      if (prefixTimer) window.clearTimeout(prefixTimer);
    };

    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable) {
        return;
      }

      if (prefix) {
        if (prefixActive && event.key.toLowerCase() === key.toLowerCase()) {
          event.preventDefault();
          clearPrefix();
          onTriggerRef.current();
          return;
        }
        if (event.key.toLowerCase() === prefix && !event.metaKey && !event.ctrlKey) {
          prefixActive = true;
          prefixTimer = window.setTimeout(clearPrefix, 1000);
          return;
        }
        return;
      }

      if (matchesModifier(event, key, meta, ctrl, shift)) {
        event.preventDefault();
        onTriggerRef.current();
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      clearPrefix();
    };
  }, [enabled, key, meta, ctrl, shift, prefix]);
}
